/**
 * HtmlContent.tsx
 * Component for safely rendering HTML content in exam questions
 * Supports LaTeX formulas, images, and rich text
 */

import React from 'react';
import { parseHtmlContent, transformImageUrls } from '@vsat/shared/lib/htmlParser';
import { parseFormulas } from './MathFormula';
import '@vsat/shared/styles/html-content.css';

interface HtmlContentProps {
  content: string;
  className?: string;
}

// Parse HTML content with LaTeX formulas
function parseHtmlWithLatex(content: string): React.ReactElement[] {
  if (!content) return [];

  // First transform localhost URLs to production URLs
  const transformedContent = transformImageUrls(content);

  // Then extract LaTeX formulas and replace them with placeholders
  const displayMathRegex = /\$\$([\s\S]*?)\$\$/g;
  const inlineMathRegex = /\$([^$\n]+?)\$/g;

  let processedContent = transformedContent;
  const formulaMap = new Map<string, string>();
  let key = 0;

  // Handle display math $$...$$
  processedContent = processedContent.replace(displayMathRegex, (match, formula) => {
    const placeholder = `__DISPLAY_MATH_${key++}__`;
    formulaMap.set(placeholder, formula);
    return placeholder;
  });

  // Handle inline math $...$
  processedContent = processedContent.replace(inlineMathRegex, (match, formula) => {
    const placeholder = `__INLINE_MATH_${key++}__`;
    formulaMap.set(placeholder, formula);
    return placeholder;
  });

  // Now parse the HTML content
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedContent;

  // Convert the DOM to React elements
  const convertNodeToReact = (
    node: any
  ): React.ReactElement | string | React.ReactElement[] | null => {
    if (node.nodeType === 3) {
      // TEXT_NODE
      const text = node.textContent || '';
      // Check if this text contains formula placeholders
      if (text.includes('__DISPLAY_MATH_') || text.includes('__INLINE_MATH_')) {
        // Split by formula placeholders and process each part
        const textParts = text.split(/(__DISPLAY_MATH_\d+__|__INLINE_MATH_\d+__)/g);
        return textParts
          .map((part: string, index: number) => {
            if (part.startsWith('__DISPLAY_MATH_') && part.endsWith('__')) {
              const formula = formulaMap.get(part);
              return formula ? (
                <React.Fragment key={`display-${key++}`}>
                  {parseFormulas(`$$${formula}$$`)}
                </React.Fragment>
              ) : (
                part
              );
            } else if (part.startsWith('__INLINE_MATH_') && part.endsWith('__')) {
              const formula = formulaMap.get(part);
              return formula ? (
                <React.Fragment key={`inline-${key++}`}>
                  {parseFormulas(`$${formula}$`)}
                </React.Fragment>
              ) : (
                part
              );
            } else {
              return part;
            }
          })
          .flat();
      }
      return text;
    } else if (node.nodeType === 1) {
      // ELEMENT_NODE
      const element = node as any;
      const tagName = element.tagName.toLowerCase();

      // Check if this is a math formula element from the portal (data-type="inline-math" or data-type="display-math")
      const dataType = element.getAttribute('data-type');
      const dataLatex = element.getAttribute('data-latex');

      if (dataType === 'inline-math' || dataType === 'display-math') {
        if (dataLatex) {
          // Decode HTML entities in LaTeX string
          let decodedLatex = dataLatex
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");

          // Add \displaystyle to inline math for better vertical alignment
          const isDisplay = dataType === 'display-math';
          if (!isDisplay && !decodedLatex.trim().startsWith('\\displaystyle')) {
            decodedLatex = `\\displaystyle ${decodedLatex.trim()}`;
          }

          // Render as formula using parseFormulas
          const formulaText = isDisplay ? `$$${decodedLatex}$$` : `$${decodedLatex}$`;
          return (
            <React.Fragment key={`math-${key++}`}>{parseFormulas(formulaText)}</React.Fragment>
          );
        }
        // If no data-latex attribute, try to get from text content (fallback)
        const textContent = element.textContent || '';
        if (textContent.trim()) {
          const isDisplay = dataType === 'display-math';
          let formula = textContent.trim();
          // Add \displaystyle to inline math
          if (!isDisplay && !formula.startsWith('\\displaystyle')) {
            formula = `\\displaystyle ${formula}`;
          }
          const formulaText = isDisplay ? `$$${formula}$$` : `$${formula}$`;
          return (
            <React.Fragment key={`math-${key++}`}>{parseFormulas(formulaText)}</React.Fragment>
          );
        }
      }

      // Special handling for table elements - wrap in scrollable container
      if (tagName === 'table') {
        const tableProps: any = { key: `table-${key++}` };

        // Copy attributes
        Array.from(element.attributes).forEach((attr: any) => {
          if (attr.name === 'class') {
            tableProps.className = attr.value;
          } else if (attr.name === 'style') {
            tableProps.style = attr.value;
          } else {
            tableProps[attr.name] = attr.value;
          }
        });

        // Convert children
        const tableChildren = Array.from(element.childNodes)
          .map((child: any) => convertNodeToReact(child))
          .filter(child => child !== null);

        const tableElement = React.createElement(tagName, tableProps, ...tableChildren);

        // Wrap table in a scrollable container with horizontal scroll
        return (
          <div key={`table-wrapper-${key++}`} className='overflow-x-auto w-full my-4'>
            {tableElement}
          </div>
        );
      }

      const props: any = { key: `element-${key++}` };

      // Copy attributes
      Array.from(element.attributes).forEach((attr: any) => {
        if (attr.name === 'class') {
          props.className = attr.value;
        } else if (attr.name === 'style') {
          props.style = attr.value;
        } else {
          props[attr.name] = attr.value;
        }
      });

      // Convert children
      const children = Array.from(element.childNodes)
        .map((child: any) => convertNodeToReact(child))
        .filter(child => child !== null);

      return React.createElement(tagName, props, ...children);
    }
    return null;
  };

  // Convert the entire content
  const reactElements = Array.from(tempDiv.childNodes)
    .map(child => convertNodeToReact(child))
    .filter((child: any) => child !== null);

  return reactElements as React.ReactElement[];
}

export function HtmlContent({ content, className }: HtmlContentProps) {
  if (!content) return null;

  if (typeof content !== 'string') {
    return <div className={className}>Invalid content type: {typeof content}</div>;
  }

  // Memoize these checks to avoid recalculating on every render
  const hasRawLatex = React.useMemo(() => /\$|\\[a-zA-Z]|\\\(|\\\[/.test(content), [content]);
  const hasMathAttributes = React.useMemo(
    () => /data-type=["'](inline-math|display-math)["']/.test(content),
    [content]
  );
  const hasLatex = hasRawLatex || hasMathAttributes;
  const hasTable = React.useMemo(() => /<table[\s>]/.test(content), [content]);

  // Memoize parsed content to prevent infinite re-renders and image reloading
  // IMPORTANT: Must memoize the entire result, not just intermediate values
  const renderedContent = React.useMemo(() => {
    if (hasLatex) {
      // parseHtmlWithLatex returns React.ReactElement[] - must be memoized
      return parseHtmlWithLatex(content);
    }
    // parseHtmlContent returns string - must be memoized
    return parseHtmlContent(content);
  }, [content, hasLatex]); // Only re-compute when content or hasLatex actually changes

  if (hasLatex) {
    return <div className={`html-content ${className || ''}`}>{renderedContent}</div>;
  }

  return (
    <div
      className={`html-content ${hasTable ? 'has-table' : ''} ${className || ''}`}
      style={hasTable ? { overflowX: 'auto', overflowY: 'visible' } : undefined}
      dangerouslySetInnerHTML={{ __html: renderedContent as string }}
    />
  );
}

export default HtmlContent;
